/**
 * frontend/src/utils/api.js
 * Single source of truth for all API calls in the TMS frontend.
 * One axios instance, one interceptor, one export per domain.
 * Constants (STATUS_COLORS etc.) belong here since they map directly
 * to values the backend stores — but they are re-exported from
 * src/utils/constants.js as well for cleaner imports in UI components.
 */
import axios from 'axios';
// Reports 5xx and dead connections. Uses fetch internally, not this client,
// so a capture failure cannot re-enter this interceptor.
import { captureError } from './captureError';

// ── Axios instance ─────────────────────────────────────────────────────────
export const api = axios.create({ baseURL: '/api' });

/**
 * A client with no interceptors, used only to exchange the refresh token.
 * Using `api` would send a failing refresh back through the handler that
 * called it.
 */
const refreshClient = axios.create({ baseURL: api.defaults.baseURL });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('tms_token'); // renamed from wms_token
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  r => r,
  async err => {
    const status = err.response?.status;

    /**
     * A 401 now usually means the 15-minute access token expired, not that
     * the session is over. Exchange the refresh token and retry once.
     *
     * `_retried` stops a loop: if the retry also 401s, the refresh token is
     * gone too and the session really has ended.
     */
    if (status === 401) {
      const refresh = localStorage.getItem('tms_refresh');
      if (refresh && !err.config?._retried) {
        try {
          const r = await refreshClient.post('/auth/refresh', { refresh_token: refresh });
          localStorage.setItem('tms_token', r.data.token);
          localStorage.setItem('tms_refresh', r.data.refresh_token);
          err.config._retried = true;
          err.config.headers = { ...err.config.headers, Authorization: `Bearer ${r.data.token}` };
          return api(err.config);
        } catch { /* the refresh token is gone too; sign out below */ }
      }
      localStorage.removeItem('tms_token');
      localStorage.removeItem('tms_refresh');
      window.location.href = '/login';
      // An ended session is not a fault. Reporting it would fill System
      // Support with the most ordinary event in the application.
      return Promise.reject(err);
    }

    // 4xx means the request was wrong — a validation failure, a permission
    // refusal, something not found. Those are answers, not faults, and the
    // page already shows them. Only 5xx and a dead connection are recorded.
    //
    // The server records its own 500s through observeResponses. What this
    // adds is the half the server cannot see: which page the person was on
    // and what they had clicked.
    if (status >= 500 || (!err.response && err.request)) {
      captureError({
        kind: 'api',
        message: `${err.config?.method?.toUpperCase() || 'REQUEST'} ${err.config?.url || ''} ${status ? `failed with ${status}` : 'got no response'}`,
        name: err.name,
        stack: err.stack,
        http_status: status || null,
        // Sent at the top level, not buried in context: the capture endpoint
        // uses it to join this report to the server's record of the same
        // failure, rather than filing a second group for one problem.
        request_id: err.response?.headers?.['x-request-id'] || null,
        api: { url: err.config?.url, method: err.config?.method },
        route: window.location?.pathname,
        context: {
          // The response body can carry a server message worth seeing. The
          // REQUEST body is deliberately not sent — it is the likeliest place
          // for a password, and captureError redacts but cannot know shapes.
          server_message: err.response?.data?.message,
          request_id: err.response?.headers?.['x-request-id'],
        },
      });
    }

    return Promise.reject(err);
  }
);

// ── Domain APIs ────────────────────────────────────────────────────────────

/**
 * The four bulk calls every soft-deletable record type has, built once.
 *
 * Seven entities times four calls is twenty-eight lines that must agree with
 * each other; this is one line that cannot disagree with itself.
 *
 *   preview   what would happen, written nowhere. The confirmation dialog
 *             is built from it: how many are mine, which are not, and what
 *             children come too
 *   delete    stage 1. Leaves the lists, sits in the Deleted view
 *   restore   stage 1 undone, bringing back whatever went down with it
 *   discard   stage 2. Leaves the Deleted view for the admin recycle bin
 *
 * None of them removes a row. That is SystemAdmin only and lives in adminAPI.
 */
const bulkFor = (base) => ({
  // `stage` is NOT optional in practice. The server works out which records
  // are in a fit state for the action, and it cannot know which action you
  // mean unless you say: a record waiting to be deleted must be live, one
  // waiting to be restored must already be deleted. Omitting it made the
  // server assume 'delete', so every preview on a Deleted view found nothing
  // in state and the bar reported "Nothing there to act on".
  preview: (ids, stage = 'delete') => api.post(`/${base}/bulk/preview`, { ids, stage }),
  delete:  (ids) => api.post(`/${base}/bulk/delete`,  { ids }),
  restore: (ids) => api.post(`/${base}/bulk/restore`, { ids }),
  discard: (ids) => api.post(`/${base}/bulk/discard`, { ids }),
});

export const projectsAPI = {
  bulk: bulkFor('projects'),
  discard:  (id) => api.delete(`/projects/${id}/discard`),
  restore:  (id) => api.post(`/projects/${id}/restore`),
  getAll:   (p)      => api.get('/projects', { params: p }),
  getById:  (id)     => api.get(`/projects/${id}`),
  getStats: (id)     => api.get(`/projects/${id}/stats`),
  create:   (d)      => api.post('/projects', d),
  update:   (id, d)  => api.put(`/projects/${id}`, d),
  delete:   (id)     => api.delete(`/projects/${id}`),
};

export const tasksAPI = {
  bulk: bulkFor('tasks'),
  discard:  (id) => api.delete(`/tasks/${id}/discard`),
  restore:  (id) => api.post(`/tasks/${id}/restore`),
  getAll:   (p)      => api.get('/tasks', { params: p }),
  getById:  (id)     => api.get(`/tasks/${id}`),
  create:   (d)      => api.post('/tasks', d),
  update:   (id, d)  => api.put(`/tasks/${id}`, d),
  delete:   (id)     => api.delete(`/tasks/${id}`),
};

export const activitiesAPI = {
  bulk: bulkFor('activities'),
  discard:  (id) => api.delete(`/activities/${id}/discard`),
  restore:  (id) => api.post(`/activities/${id}/restore`),
  getById: (id) => api.get(`/activities/${id}`),
  getByTask:        (tid)    => api.get('/activities', { params: { task_id: tid } }),
  getAll:           (p)      => api.get('/activities', { params: p }),
  create:           (d)      => api.post('/activities', d),
  update:           (id, d)  => api.put(`/activities/${id}`, d),
  delete:           (id)     => api.delete(`/activities/${id}`),
  getEffortSummary: (p)      => api.get('/activities/effort-summary', { params: p }),
  // The history of a repeating activity: which days it falls on in the
  // window, and what became of each. Writes go through workplanAPI.setOccurrence.
  getOccurrences:   (id, p)  => api.get(`/activities/${id}/occurrences`, { params: p }),
};

export const bugsAPI = {
  bulk: bulkFor('bugs'),
  restore:  (id) => api.post(`/bugs/${id}/restore`),
  discard:  (id) => api.delete(`/bugs/${id}/discard`),
  getAll:   (p)      => api.get('/bugs', { params: p }),
  getById:  (id)     => api.get(`/bugs/${id}`),
  create:   (d)      => api.post('/bugs', d),
  update:   (id, d)  => api.put(`/bugs/${id}`, d),
  delete:       (id) => api.delete(`/bugs/${id}`),
  byProject:    (p) => api.get('/bugs/by-project', { params: p }),
};

export const clientsAPI = {
  bulk: bulkFor('clients'),
  discard:  (id) => api.delete(`/clients/${id}/discard`),
  restore:  (id) => api.post(`/clients/${id}/restore`),
  getAll:        (p)      => api.get('/clients', { params: p }),
  getById:       (id)     => api.get(`/clients/${id}`),
  create:        (d)      => api.post('/clients', d),
  update:        (id, d)  => api.put(`/clients/${id}`, d),
  delete:        (id)     => api.delete(`/clients/${id}`),
  linkProject:   (id, d)  => api.post(`/clients/${id}/link-project`, d),
  unlinkProject: (id, d)  => api.post(`/clients/${id}/unlink-project`, d),
};

export const changeRequestsAPI = {
  bulk: bulkFor('change-requests'),
  restore:  (id) => api.post(`/change-requests/${id}/restore`),
  discard:  (id) => api.delete(`/change-requests/${id}/discard`),
  submitForReview: (id) => api.put(`/change-requests/${id}/submit`),
  getAll:   (p)      => api.get('/change-requests', { params: p }),
  getById:  (id)     => api.get(`/change-requests/${id}`),
  create:   (d)      => api.post('/change-requests', d),
  update:   (id, d)  => api.put(`/change-requests/${id}`, d),
  delete:       (id) => api.delete(`/change-requests/${id}`),
  byProject:    (p) => api.get('/change-requests/by-project', { params: p }),
};

export const trainingAPI = {
  bulk: bulkFor('training'),
  discard:  (id) => api.delete(`/training/${id}/discard`),
  restore:  (id) => api.post(`/training/${id}/restore`),
  getAll:   (p)      => api.get('/training', { params: p }),
  getById:  (id)     => api.get(`/training/${id}`),
  create:   (d)      => api.post('/training', d),
  update:   (id, d)  => api.put(`/training/${id}`, d),
  delete:   (id)     => api.delete(`/training/${id}`),
};

// ── Dashboard API (consolidated - was split across 4 files) ────────────────
// All /api/dashboard/* endpoints. Previously dashboardAPI + dashboardExtraAPI
// have been merged into the single /routes/dashboard.js backend file; this
// mirrors that on the frontend so every component imports one object.
export const dashboardAPI = {
  // Core stats (personalized by role)
  getGlobal:             ()         => api.get('/dashboard/global'),
  getProject:            (id)       => api.get(`/dashboard/project/${id}`),
  // Personalized / team-pulse widgets
  getHealthSummary:      ()         => api.get('/dashboard/health-summary'),
  getOverdueSummary:     ()         => api.get('/dashboard/overdue-summary'),
  getUpcomingMilestones: (limit)    => api.get('/dashboard/upcoming-milestones', { params: { limit } }),
  getOpenRisks:          (limit)    => api.get('/dashboard/open-risks', { params: { limit } }),
  getActiveTimers:       ()         => api.get('/dashboard/active-timers'),
  getMyTasks:            ()         => api.get('/dashboard/my-tasks'),
  getRecentActivity:     (limit)    => api.get('/dashboard/recent-activity', { params: { limit } }),
  getPendingApprovals:   ()         => api.get('/dashboard/pending-approvals'),
  decideApproval:        (id, dec)  => api.put(`/dashboard/pending-approvals/${id}`, { decision: dec }),
  getEffortTrend:        (period)   => api.get('/dashboard/effort-trend', { params: { period } }),
  // Planned due dates against actual completion. period is one of
  // 48h | 1W | 1M | 3M | 1Y | all; anything else is treated as 1W.
  getExecutionProfile:   (period)   => api.get('/dashboard/execution-profile', { params: { period } }),
  getTeamWorkload:       ()         => api.get('/dashboard/team-workload'),
};

/**
 * Knowledge Base.
 *
 * A document is a subject that holds several files and belongs to several
 * modules. Reading is open to anyone who can reach the page; every write is
 * refused server side for anything below Admin level.
 */
export const knowledgeAPI = {
  getAll:      (p)      => api.get('/knowledge', { params: p }),
  getModules:  ()       => api.get('/knowledge/modules'),
  // What may be uploaded and how large, so the form states it rather than
  // describing it. The route is the single source of truth.
  getLimits:   ()       => api.get('/knowledge/limits'),
  downloadFile:(fileId) => api.get(`/knowledge/files/${fileId}/download`, { responseType: 'blob' }),
  create:      (fd)     => api.post('/knowledge', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  addFiles:    (id, fd) => api.post(`/knowledge/${id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeFile:  (fileId) => api.delete(`/knowledge/files/${fileId}`),
  update:      (id, d)  => api.put(`/knowledge/${id}`, d),
  remove:      (id)     => api.delete(`/knowledge/${id}`),
  restore:     (id)     => api.post(`/knowledge/${id}/restore`),
};

export const reportsAPI = {
  getWeekly:     (p) => api.get('/reports/weekly',     { params: p }),
  getMonthly:    (p) => api.get('/reports/monthly',    { params: p }),
  getComparison: ()  => api.get('/reports/comparison'),
  getTeam:       (p) => api.get('/reports/team',       { params: p }),
};

export const exportAPI = {
  exportProjects:       ()    => api.get('/export/projects',        { responseType: 'blob' }),
  exportTasks:          (pid) => api.get('/export/tasks',           { params: { project_id: pid }, responseType: 'blob' }),
  exportBugs:           (pid) => api.get('/export/bugs',            { params: { project_id: pid }, responseType: 'blob' }),
  exportChangeRequests: (pid) => api.get('/export/change-requests', { params: { project_id: pid }, responseType: 'blob' }),
  exportActivities:     (pid) => api.get('/export/activities',      { params: { project_id: pid }, responseType: 'blob' }),
  exportTraining:       (pid) => api.get('/export/training',        { params: { project_id: pid }, responseType: 'blob' }),
  exportReport:         (p)   => api.get('/export/report',          { params: p, responseType: 'blob' }),
};

export const usersAPI = {
  getAll:         ()                       => api.get('/users'),
  getById:        (id)                     => api.get(`/users/${id}`),
  create:         (d)                      => api.post('/auth/register', d),
  update:         (id, d)                  => api.put(`/users/${id}`, d),
  changePassword: (id, currentPw, newPw)  => api.put(`/users/${id}/password`, { current_password: currentPw, new_password: newPw }),
  deactivate:     (id)                     => api.delete(`/users/${id}`),
  lock:           (id)                     => api.put(`/users/${id}/lock`),
  unlock:         (id)                     => api.put(`/users/${id}/unlock`),
  adminResetPw:   (id, pw)                 => api.put(`/users/${id}/reset-password`, { new_password: pw }),
};

// Chat API - notifications endpoints removed (moved to notificationsAPI below)
export const chatAPI = {
  getGroups:     ()          => api.get('/chat/groups'),
  createGroup:   (d)         => api.post('/chat/groups', d),
  addMembers:    (gid, uids) => api.post(`/chat/groups/${gid}/members`, { user_ids: uids }),
  deleteGroup:   (gid)       => api.delete(`/chat/groups/${gid}`),
  getMessages:   (gid, p)    => api.get(`/chat/groups/${gid}/messages`, { params: p }),
  sendText:      (gid, content, replyTo) => api.post(`/chat/groups/${gid}/messages`, { content, reply_to: replyTo }),
  sendFile:      (gid, fd)   => api.post(`/chat/groups/${gid}/messages`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteMessage: (mid)       => api.delete(`/chat/messages/${mid}`),
};

// Notifications API - owns all /api/notifications/* endpoints
// getNotifications / markNotificationsRead previously lived in chatAPI but
// were removed when dedicated notifications routes were created.
export const notificationsAPI = {
  getAll:      (params)    => api.get('/notifications', { params }),
  getCounts:   ()          => api.get('/notifications/counts'),
  markRead:    (id)        => api.put(`/notifications/${id}/read`),
  markAllRead: (category)  => api.put('/notifications/read-all', null,
                               { params: category ? { category } : {} }),
  delete:      (id)        => api.delete(`/notifications/${id}`),
};

export const workplanAPI = {
  reorder:       (ids) => api.put('/workplan/reorder', { ids }),
  getMonth:      (p) => api.get('/workplan/month', { params: p }),
  get:                (p)            => api.get('/workplan', { params: p }),
  getTodo:            (p)            => api.get('/workplan/todo', { params: p }),
  getTeam:            (p)            => api.get('/workplan/team', { params: p }),
  copyWeek:           (d)            => api.post('/workplan/copy-week', d),
  setOccurrence:      (d)            => api.put('/workplan/occurrence', d),
  create:             (d)            => api.post('/workplan', d),
  confirm:            (d)            => api.post('/workplan/confirm', d),
  update:             (id, d)        => api.put(`/workplan/${id}`, d),
  delete:             (id)           => api.delete(`/workplan/${id}`),
  updateTaskDate:     (taskId, d)    => api.put(`/workplan/task-date/${taskId}`, d),
  updateActivityDate: (activityId, d)=> api.put(`/workplan/activity-date/${activityId}`, d),
};

export const projectTypesAPI = {
  getAll:             ()          => api.get('/project-types'),
  getById:            (id)        => api.get(`/project-types/${id}`),
  create:             (d)         => api.post('/project-types', d),
  update:             (id, d)     => api.put(`/project-types/${id}`, d),
  getMilestones:      (pid)       => api.get(`/project-types/milestones/${pid}`),
  createMilestone:    (pid, d)    => api.post(`/project-types/milestones/${pid}`, d),
  updateMilestone:    (id, d)     => api.put(`/project-types/milestones/item/${id}`, d),
  deleteMilestone:    (id)        => api.delete(`/project-types/milestones/item/${id}`),
  getRisks:           (pid)       => api.get(`/project-types/risks/${pid}`),
  createRisk:         (pid, d)    => api.post(`/project-types/risks/${pid}`, d),
  updateRisk:         (id, d)     => api.put(`/project-types/risks/item/${id}`, d),
  deleteRisk:         (id)        => api.delete(`/project-types/risks/item/${id}`),
  getIssues:          (pid)       => api.get(`/project-types/issues/${pid}`),
  createIssue:        (pid, d)    => api.post(`/project-types/issues/${pid}`, d),
  updateIssue:        (id, d)     => api.put(`/project-types/issues/item/${id}`, d),
  deleteIssue:        (id)        => api.delete(`/project-types/issues/item/${id}`),
  getComments:        (pid, p)    => api.get(`/project-types/comments/${pid}`, { params: p }),
  createComment:      (pid, d)    => api.post(`/project-types/comments/${pid}`, d),
  deleteComment:      (id)        => api.delete(`/project-types/comments/item/${id}`),
  getHealth:          (pid)       => api.get(`/project-types/health/${pid}`),
  getStatusHistory:   (pid)       => api.get(`/project-types/status-history/${pid}`),
  getWorkflowStatuses:(ptId)      => api.get(`/project-types/workflow-statuses/${ptId}`),
  generate:           (pid, d)    => api.post(`/project-types/generate/${pid}`, d),
};

export const adminAPI = {
  // Recycle bin. Restore is Admin; permanent delete is SystemAdmin, matching
  // the purge it sits beside.
  // Takes { entity, limit, offset }. Clearing an entity can put thousands of
  // rows in the bin at once, so the page asks for them a screen at a time.
  getRecycleBin:    (p)            => api.get('/admin/recycle-bin', { params: p }),
  getRecycleBinCounts: ()          => api.get('/admin/recycle-bin/counts'),
  getEmailLog:      (p)            => api.get('/admin/email-log', { params: p }),
  restoreRecord:    (entity, id)   => api.post(`/admin/recycle-bin/${entity}/${id}/restore`),
  purgeRecord:      (entity, id)   => api.delete(`/admin/recycle-bin/${entity}/${id}`),

  // Many at once. `items` is [{ entity, id }], mixed types allowed, 500 max.
  // Each call is one transaction: all of it happens or none of it does.
  bulkRestoreRecords: (items)      => api.post('/admin/recycle-bin/restore', { items }),
  // The one action with no undo, so the word DELETE is typed, not clicked.
  bulkPurgeRecords:   (items)      => api.delete('/admin/recycle-bin/bulk',
                                        { data: { items, confirm: 'DELETE' } }),

  // Clearing a whole record type. SystemAdmin only. Preview first, always:
  // the dialog has to be able to say how many, and what comes with them.
  clearPreview:     (entity)       => api.get(`/admin/clear/${entity}/preview`),
  clearEntity:      (entity)       => api.post(`/admin/clear/${entity}`, { confirm: entity }),
  getDashboard:    ()         => api.get('/admin/dashboard'),
  getAuditLogs:    (p)        => api.get('/admin/audit-logs', { params: p }),
  getSettings:     ()         => api.get('/admin/settings'),
  updateSetting:   (key, val) => api.put(`/admin/settings/${key}`, { value: val }),
  getUsers:        (p)        => api.get('/admin/users', { params: p }),
  getRoles:        ()         => api.get('/admin/roles'),
  purgeData:       (d)        => api.delete('/admin/purge', { data: d }),
  getStatsOverTime:(months)   => api.get('/admin/stats/over-time', { params: months ? { months } : {} }),
};

export const attachmentsAPI = {
  getAll:    (entityType, entityId) => api.get(`/attachments/${entityType}/${entityId}`),
  upload:    (entityType, entityId, formData) =>
               api.post(`/attachments/${entityType}/${entityId}`, formData,
                        { headers: { 'Content-Type': 'multipart/form-data' } }),
  download:  (id)  => api.get(`/attachments/download/${id}`, { responseType: 'blob' }),
  delete:    (id)  => api.delete(`/attachments/${id}`),
};

export const timeTrackingAPI = {
  start:     (activityId)       => api.post(`/time-tracking/${activityId}/start`),
  stop:      (activityId)       => api.post(`/time-tracking/${activityId}/stop`),
  getLogs:   (activityId)       => api.get(`/time-tracking/${activityId}/logs`),
  getRunning:()                 => api.get('/time-tracking/running'),
  logManual: (activityId, data) => api.post(`/time-tracking/${activityId}/log-manual`, data),
  updateLog: (logId, data)      => api.put(`/time-tracking/log/${logId}`, data),
  deleteLog: (logId)            => api.delete(`/time-tracking/log/${logId}`),
};

export const customFieldsAPI = {
  getAll:  (appliesTo, projectTypeId) =>
             api.get(`/custom-fields/${appliesTo}`, { params: { project_type_id: projectTypeId } }),
  create:  (d)      => api.post('/custom-fields', d),
  update:  (id, d)  => api.put(`/custom-fields/${id}`, d),
  delete:  (id)     => api.delete(`/custom-fields/${id}`),
};

export const taskDependenciesAPI = {
  getFor:  (taskId) => api.get(`/task-dependencies/${taskId}`),
  create:  (d)      => api.post('/task-dependencies', d),
  delete:  (id)     => api.delete(`/task-dependencies/${id}`),
};

export const tagsAPI = {
  getAll:  ()                              => api.get('/tags'),
  create:  (d)                             => api.post('/tags', d),
  getFor:  (entityType, entityId)          => api.get(`/tags/for/${entityType}/${entityId}`),
  attach:  (d)                             => api.post('/tags/attach', d),
  detach:  (d)                             => api.delete('/tags/detach', { data: d }),
};

export const searchAPI = {
  query: (q) => api.get('/search', { params: { q } }),
};

// ── Utility helpers ────────────────────────────────────────────────────────

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
};

// ── Page access control ────────────────────────────────────────────────────
export const pageAccessAPI = {
  getAll:     ()                              => api.get('/page-access'),
  getMyPages: ()                              => api.get('/page-access/my-pages'),
  toggle:     (pageId, roleName, type, value) => api.put('/page-access/' + pageId + '/' + roleName, { type, value }),
};

// ── Project role management ────────────────────────────────────────────────
export const projectRolesAPI = {
  getPermissions:   ()              => api.get('/project-roles/permissions'),
  getDefinitions:   (projectType)   => api.get('/project-roles/definitions', { params: { project_type: projectType } }),
  getForProject:    (projectId)     => api.get('/project-roles/' + projectId),
  getMyPermissions: (projectId)     => api.get('/project-roles/' + projectId + '/my-permissions'),
  assign:           (projectId, d)  => api.post('/project-roles/' + projectId, d),
  remove:           (assignmentId)  => api.delete('/project-roles/' + assignmentId),
  updateGrant:      (roleId, d)     => api.put('/project-roles/grants/' + roleId, d),
  getAllRoles:      ()              => api.get('/project-roles/definitions/all'),
  createRole:       (d)             => api.post('/project-roles/definitions', d),
  deleteRole:       (roleId)        => api.delete('/project-roles/definitions/' + roleId),
};

// ── Project stage workflow ─────────────────────────────────────────────────
export const projectStagesAPI = {
  getDefinitions: (projectType)  => api.get('/project-stages/definitions', { params: { project_type: projectType } }),
  advance:        (projectId, d) => api.put('/project-stages/' + projectId + '/advance', d),
  setStage:       (projectId, d) => api.put('/project-stages/' + projectId + '/set', d),
  getHistory:     (projectId)    => api.get('/project-stages/' + projectId + '/history'),
  lockBaseline:   (projectId, d) => api.put('/project-stages/' + projectId + '/baseline', d),
};

// ── Profile (own account) ─────────────────────────────────────────────────
export const profileAPI = {
  get:            ()  => api.get('/profile'),
  update:         (d) => api.put('/profile', d),
  changePassword: (d) => api.put('/profile/password', d),
};

// ── Delegations ───────────────────────────────────────────────────────────
export const delegationsAPI = {
  getAll:  ()    => api.get('/delegations'),
  create:  (d)   => api.post('/delegations', d),
  accept:  (id)  => api.put('/delegations/' + id + '/accept'),
  decline: (id)  => api.put('/delegations/' + id + '/decline'),
  revoke:  (id)  => api.put('/delegations/' + id + '/revoke'),
  remove:  (id)  => api.delete('/delegations/' + id),
};

// Password reset (public — no auth needed)
export const passwordResetAPI = {
  forgot: (email)     => api.post('/auth/forgot-password', { email }),
  reset:  (token, pw) => api.post('/auth/reset-password', { token, password: pw }),
};

// Platforms directory — read by everyone, written only by Admins.
// A logo upload has to go as multipart; everything else is plain JSON.
const asMultipart = (d) =>
  d instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined;

// System Support — error tracking for the TMS itself. Not client products:
// those are bugs and change requests, via Dev Hub.
export const systemSupportAPI = {
  getAll:   (params)     => api.get('/system-support', { params }),
  getStats: ()           => api.get('/system-support/stats'),
  exportXlsx: (params) => api.get('/system-support/export', { params, responseType: 'blob' }),
  getById:  (id)         => api.get(`/system-support/${id}`),
  update:   (id, data)   => api.put(`/system-support/${id}`, data),
};

export const platformsAPI = {
  getAll: (p)     => api.get('/platforms', { params: p }),
  create: (d)     => api.post('/platforms', d, asMultipart(d)),
  update: (id, d) => api.put(`/platforms/${id}`, d, asMultipart(d)),
  delete: (id)    => api.delete(`/platforms/${id}`),
};

export const mastersAPI = {
  getAll:     (category) => api.get('/masters', category ? { params:{ category } } : {}),
  getCategory:(cat)      => api.get(`/masters/${cat}`),
  create:     (d)        => api.post('/masters', d),
  update:     (id, d)    => api.put(`/masters/${id}`, d),
  delete:     (id)       => api.delete(`/masters/${id}`),
};
